/**
 * Client-safe peel order (no Node crypto). Shared by API domain + Scene Studio UI.
 * Front→back: higher ymax first; tie-break smaller area, then id.
 */

export interface HeuristicPeelOrderObjectRef {
	id: string;
	/** VL / product bbox [xmin, ymin, xmax, ymax] in 0–1000. */
	bbox: readonly [number, number, number, number];
}

export function computeHeuristicPeelOrder(
	objects: readonly HeuristicPeelOrderObjectRef[],
): string[] {
	const ranked = [...objects].sort((a, b) => {
		const [, , , aYmax] = a.bbox;
		const [, , , bYmax] = b.bbox;
		if (bYmax !== aYmax) {
			return bYmax - aYmax;
		}
		const aArea =
			Math.max(0, a.bbox[2] - a.bbox[0]) *
			Math.max(0, a.bbox[3] - a.bbox[1]);
		const bArea =
			Math.max(0, b.bbox[2] - b.bbox[0]) *
			Math.max(0, b.bbox[3] - b.bbox[1]);
		if (aArea !== bArea) {
			return aArea - bArea;
		}
		return a.id.localeCompare(b.id);
	});
	return ranked.map((o) => o.id);
}

/** Swap neighbor in peel order (front = index 0). Returns new array. */
export function movePeelOrderId(params: {
	peelOrder: readonly string[];
	objectId: string;
	direction: "up" | "down";
}): string[] {
	const next = [...params.peelOrder];
	const index = next.indexOf(params.objectId);
	if (index < 0) {
		return next;
	}
	const swapWith = params.direction === "up" ? index - 1 : index + 1;
	if (swapWith < 0 || swapWith >= next.length) {
		return next;
	}
	const tmp = next[index];
	next[index] = next[swapWith];
	next[swapWith] = tmp;
	return next;
}

export function countTickedExtractTargets(
	selection: Record<string, string>,
): number {
	return Object.values(selection).filter(
		(t) => t === "EXTRACT_2D" || t === "EXTRACT_3D",
	).length;
}
